import React, { useState } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast, errorToast } from "../../../utils/dialog";

const FacilitiesAdmin = () => {
  const { facilitiesData, addFacility, updateFacility, deleteFacility } = useData();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, image: previewUrl }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await updateFacility(editingId, formData, imageFile);
        successToast("Facility updated.");
      } else {
        await addFacility(formData, imageFile);
        successToast("Facility added.");
      }
      resetForm();
    } catch (err) {
      console.error(err);
      errorToast("Error saving facility data.");
    }
  };

  const handleEdit = (facility) => {
    setFormData(facility);
    setEditingId(facility.id);
    setImageFile(null); // Reset the image file
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete facility?",
        text: "This facility will be permanently removed.",
      })
    ) {
      try {
        await deleteFacility(id);
        successToast("Facility deleted.");
      } catch (err) {
        console.error(err);
        errorToast("Error deleting facility.");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image: "",
    });
    setEditingId(null);
    setImageFile(null);
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold mb-4 text-gray-800">Facilities Admin Panel</h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">
          {editingId ? "Edit Facility" : "Add Facility"}
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Facility title"
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Facility description"
            rows={3}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <ImageUploadField
          label="Image"
          hint="Recommended size: 100×100px (square)"
          aspect="square"
          previewUrl={formData.image}
          file={imageFile}
          onChange={handleFileChange}
          onClear={() => {
            setImageFile(null);
            setFormData((prev) => ({ ...prev, image: "" }));
          }}
        />
        <div className="flex justify-end space-x-4">
          <button
            type="submit"
            className="px-6 py-2 bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] rounded transition font-medium"
          >
            {editingId ? "Update Facility" : "Add Facility"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Existing Facilities */}
      <div className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">Existing Facilities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {facilitiesData.map((facility) => (
            <div key={facility.id} className="border rounded p-4 shadow">
              <img
                src={facility.image || "https://via.placeholder.com/150"}
                alt={facility.title}
                className="w-full h-40 object-cover rounded mb-4"
              />
              <h4 className="text-lg font-bold">{facility.title}</h4>
              <p className="text-sm text-gray-600">{facility.description}</p>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => handleEdit(facility)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(facility.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FacilitiesAdmin;